'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { AlertTriangle, ImageOff, XCircle } from 'lucide-react';
import { AdaptiveDialog } from '@/shared/ui/AdaptiveDialog';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { Label } from '@/shared/ui/shadcn/label';
import { Typography } from '@/shared/ui/shadcn/typography';
import {
  getProjectAngleValidationStatus,
  type ProjectAngle,
} from '@/types/project-angle';
import { MaterialTypeSelector } from '@/features/project-reference/ui/MaterialTypeSelector';
import type { MaterialCardType } from '@/features/project-reference/hooks/use-project-references-panel';
import { cn } from '@/shared/lib/cn';

const MAX_DESCRIPTION_LENGTH = 500;

interface AngleEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The angle being edited — drives the hero preview and the validation note.
   *  Nullable because the dialog is mounted permanently and only fed an angle
   *  when opened. */
  angle: ProjectAngle | null;
  description: string;
  onSave: (description: string) => void;
  onDelete?: () => void;
  // Change the material type: re-file this View as any other Brief material kind
  // (Doc / Reference / Output style) via the shared type selector. Omit to hide
  // the affordance.
  onChangeType?: (target: MaterialCardType) => void;
  isSaving?: boolean;
  isDeleting?: boolean;
  isConverting?: boolean;
}

export function AngleEditDialog({
  open,
  onOpenChange,
  angle,
  description: initialDescription,
  onSave,
  onDelete,
  onChangeType,
  isSaving = false,
  isDeleting = false,
  isConverting = false,
}: AngleEditDialogProps) {
  const t = useTranslations('angles');
  const tCommon = useTranslations('common');

  const [description, setDescription] = useState(initialDescription);

  useEffect(() => {
    if (open) {
      setDescription(initialDescription);
    }
  }, [open, initialDescription]);

  const handleSave = () => {
    onSave(description.trim());
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    if (value.length <= MAX_DESCRIPTION_LENGTH) {
      setDescription(value);
    }
  };

  const fileUrl = angle?.attributes.file_url;
  const validationStatus = angle
    ? getProjectAngleValidationStatus(angle)
    : null;
  const isProhibited = validationStatus === 'prohibited';
  // A `different_building` angle never reaches this dialog anymore: lenient
  // intake auto-routes it to References (the angle is soft-deleted) and strict
  // intake hard-blocks it in the concept add-view flow — neither leaves a flagged
  // View tile in the library. So the old in-dialog "It's my building / Create new
  // building / re-type" escape hatches were dead and have been removed; re-typing
  // a view to a reference/output style still lives in the 4-way MaterialTypeSelector
  // below. The validation note covers the genuine rejection states that DO surface.
  const isValidationError =
    validationStatus === 'invalid' ||
    validationStatus === 'prohibited' ||
    validationStatus === 'technical_error';
  // Validation copy is server-driven (API recommendations); moved here from the
  // old list row so a rejected/flagged angle still explains what went wrong.
  const validationMessage = isValidationError
    ? angle?.attributes.validation.recommendations || null
    : null;

  return (
    <AdaptiveDialog
      open={open}
      onClose={() => onOpenChange(false)}
      title={t('editAngle')}
      onSave={handleSave}
      onDelete={onDelete}
      isSaving={isSaving}
      isDeleting={isDeleting}
      saveLabel={isSaving ? tCommon('saving') : tCommon('save')}
    >
      <div className="space-y-4">
        {/* Hero preview — the only large view of the source photo now that the
            library shows compact tiles and the full-screen viewer is gone.
            Mirrors Apple's AngleEditSheet hero image. */}
        <div className="relative h-[220px] w-full overflow-hidden rounded-lg bg-muted">
          {fileUrl ? (
            <img
              src={fileUrl}
              alt={t('editAngle')}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground">
              <ImageOff className="h-8 w-8" />
            </div>
          )}
        </div>

        {/* Validation note under the hero (server-driven copy). */}
        {validationMessage && (
          <div
            className={cn(
              'flex items-start gap-2',
              isProhibited ? 'text-destructive' : 'text-warning'
            )}
          >
            {isProhibited ? (
              <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
            ) : (
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            )}
            <Typography variant="small" className="!mt-0">
              {validationMessage}
            </Typography>
          </div>
        )}

        {/* Change the material type: the shared 4-way selector (View / Document /
            Reference / Output style), identical to the reference detail dialog.
            Sits above the description — switching what the item is used for is the
            primary action; the description is secondary and always sits last. The
            current type is always View (this dialog edits an angle). The server
            validates and rejects with a verbatim message (e.g. "view has
            renders") — no client-side gating here. */}
        {onChangeType && (
          <MaterialTypeSelector
            currentType="view"
            options={['view', 'document', 'reference', 'output']}
            busy={isConverting}
            disabled={isConverting || isSaving || isDeleting}
            onSelect={onChangeType}
          />
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="angle-description">{t('descriptionLabel')}</Label>
            <Typography
              variant="muted"
              className={cn(
                'text-card-meta',
                description.length >= MAX_DESCRIPTION_LENGTH && 'text-warning'
              )}
            >
              {description.length}/{MAX_DESCRIPTION_LENGTH}
            </Typography>
          </div>
          <Textarea
            id="angle-description"
            value={description}
            onChange={handleDescriptionChange}
            rows={4}
            maxLength={MAX_DESCRIPTION_LENGTH}
            placeholder={t('descriptionPlaceholder')}
          />
          <Typography variant="muted" className="text-card-meta">
            {t('descriptionHelper')}
          </Typography>
        </div>
      </div>
    </AdaptiveDialog>
  );
}
